import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { getApiBaseUrl, getAccessTokenForApi } from '../api/client';
import { waitForAccessToken } from './waitForAccessToken';

let connection: HubConnection | null = null;
let startPromise: Promise<void> | null = null;
let refCount = 0;
let teardownTimer: ReturnType<typeof setTimeout> | null = null;

/** Grace period so StrictMode's synchronous unmount/remount reuses the connection. */
const TEARDOWN_DELAY_MS = 250;

/** Throws when empty so negotiate never sends `Authorization: Bearer ` (API 401). */
export function notificationHubAccessToken(): string {
  const token = getAccessTokenForApi();
  if (!token) {
    throw new Error('Notification hub has no access token');
  }
  return token;
}

function build(): HubConnection {
  return new HubConnectionBuilder()
    .withUrl(`${getApiBaseUrl()}/signalr`, {
      // Read lazily on every negotiate and reconnect so silent renew is picked up.
      accessTokenFactory: () => notificationHubAccessToken(),
      // Bearer auth, not cookies — keeps the server's AllowAnyOrigin policy valid.
      withCredentials: false,
    })
    .withAutomaticReconnect([0, 2000, 10000, 30000])
    .configureLogging(import.meta.env.DEV ? LogLevel.Information : LogLevel.Warning)
    .build();
}

export function acquireNotificationHub(): HubConnection {
  if (teardownTimer) {
    clearTimeout(teardownTimer);
    teardownTimer = null;
  }
  connection ??= build();
  refCount += 1;
  startPromise ??= waitForAccessToken(getAccessTokenForApi)
    .then(() => connection!.start())
    .catch((error) => {
      startPromise = null;
      throw error;
    });
  return connection;
}

export function releaseNotificationHub(): void {
  refCount = Math.max(0, refCount - 1);
  if (refCount > 0 || teardownTimer) return;

  teardownTimer = setTimeout(() => {
    teardownTimer = null;
    if (refCount > 0 || !connection) return;

    const closing = connection;
    const pending = startPromise;
    connection = null;
    startPromise = null;
    // Never stop a connection whose start() is still in flight.
    void Promise.resolve(pending)
      .catch(() => undefined)
      .then(() => closing.stop());
  }, TEARDOWN_DELAY_MS);
}

/** Exposed for tests — resets module state between cases. */
export function resetNotificationHubForTests(): void {
  if (teardownTimer) {
    clearTimeout(teardownTimer);
    teardownTimer = null;
  }
  const closing = connection;
  connection = null;
  startPromise = null;
  refCount = 0;
  if (closing) {
    void closing.stop().catch(() => undefined);
  }
}

export function getNotificationHubConnectionState(): string {
  return connection?.state ?? 'Disconnected';
}

/** Resolves when the acquired hub has finished its current start attempt (success or failure). */
export function whenNotificationHubStarted(): Promise<void> {
  return startPromise ?? Promise.resolve();
}
